import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useApi } from '../hooks/useApi';
import type { FileRecord, TableMeta } from '../services/api';
import { LayoutGrid, MessageSquare, SlidersHorizontal, Download, ArrowLeft, FileSpreadsheet } from 'lucide-react';
import Visualiser from '../components/Visualiser';
import ChatPanel from '../components/ChatPanel';
import OperationsPanel from '../components/OperationsPanel';
import ExportPanel from '../components/ExportPanel';
import './WorkspacePage.css';

type Tab = 'visualiser' | 'chat' | 'operations' | 'export';

export default function WorkspacePage() {
  const { fileId } = useParams<{ fileId: string }>();
  const api = useApi();
  const navigate = useNavigate();

  const [file, setFile] = useState<FileRecord | null>(null);
  const [tables, setTables] = useState<TableMeta[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState<Tab>('visualiser');
  const [selectedTableId, setSelectedTableId] = useState<string | null>(null);
  const [allFiles, setAllFiles] = useState<{ file_id: string; file_name: string }[]>([]);
  const [activeSessionId, setActiveSessionId] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!fileId) return;
    try {
      const [fileRes, tablesRes, filesRes] = await Promise.all([
        api.get(`/excel/files/${fileId}`),
        api.get(`/excel/files/${fileId}/tables`),
        api.get('/excel/files'),
      ]);
      setFile(fileRes.data);
      setTables(tablesRes.data.tables || []);
      setAllFiles((filesRes.data.files || []).map((f: any) => ({ file_id: f.file_id, file_name: f.file_name })));
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Failed to load file');
    } finally {
      setLoading(false);
    }
  }, [api, fileId]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="ws-loading"><span className="spinner" /> Loading workspace...</div>;
  if (error) return (
    <div className="ws-error">
      <p>{error}</p>
      <button className="btn btn-secondary" onClick={() => navigate('/dashboard')}>← Back</button>
    </div>
  );
  if (!file) return null;

  const TABS: { id: Tab; label: string; icon: React.ReactNode }[] = [
    { id: 'visualiser', label: 'Visualiser', icon: <LayoutGrid size={16} /> },
    { id: 'chat', label: 'Chat', icon: <MessageSquare size={16} /> },
    { id: 'operations', label: 'Operations', icon: <SlidersHorizontal size={16} /> },
    { id: 'export', label: 'Export', icon: <Download size={16} /> },
  ];

  return (
    <div className="workspace">
      <div className="ws-header">
        <button className="btn btn-ghost ws-back" onClick={() => navigate('/dashboard')}>
          <ArrowLeft size={14} /> Back
        </button>
        <div className="ws-file-info">
          <FileSpreadsheet size={18} className="ws-file-icon" />
          <span className="ws-file-name">{file.file_name}</span>
          <div className="ws-badges">
            <span className="badge badge-accent">{file.total_tables} tables</span>
            <span className="badge badge-accent">{file.sheets?.length} sheets</span>
            {file.total_formulas > 0 && <span className="badge badge-yellow">{file.total_formulas} formulas</span>}
          </div>
        </div>
      </div>

      <div className="ws-tabs">
        {TABS.map(t => (
          <button
            key={t.id}
            className={`ws-tab ${activeTab === t.id ? 'active' : ''}`}
            onClick={() => setActiveTab(t.id)}
          >
            {t.icon}
            <span>{t.label}</span>
          </button>
        ))}
      </div>

      <div className="ws-body">
        {activeTab === 'visualiser' && (
          <Visualiser
            file={file}
            tables={tables}
            selectedTableId={selectedTableId}
            onSelectTable={setSelectedTableId}
          />
        )}
        {activeTab === 'chat' && (
          <ChatPanel fileId={fileId!} tables={tables} allFiles={allFiles} activeSessionId={activeSessionId} onSessionChange={setActiveSessionId} />
        )}
        {activeTab === 'operations' && (
          <OperationsPanel tables={tables} />
        )}
        {activeTab === 'export' && (
          <ExportPanel fileId={fileId!} tables={tables} />
        )}
      </div>
    </div>
  );
}
